'use client';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import '@/styles/player-swiper.css';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

import {
  getYouTubeVideoData,
  isValidYoutubeVideo,
} from '@/modules/players/v2/tabs/videos/videoUtils';
import { YoutubeCards } from '@/modules/players/v2/tabs/videos/YoutubeCards';
import { PlayerVideosData } from '@/types/players.types';
import { getWithBaseUrl } from '@/utils/helpers/getWithBaseUrl';

interface PlayerVideosProps {
  playerVideos: PlayerVideosData[];
  slug: string;
}

export const SwiperNavButtons = () => {
  const swiper = useSwiper();
  return (
    <>
      <button className='custom-prev-button' onClick={() => swiper.slidePrev()}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <button className='custom-next-button' onClick={() => swiper.slideNext()}>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </>
  );
};

export const PlayerVideos = ({ playerVideos, slug }: PlayerVideosProps) => {
  const validVideos = playerVideos.filter((video) => isValidYoutubeVideo(video.link));

  if (validVideos.length === 0) {
    return null;
  }

  return (
    <>
      <div className='mb-6 mt-16 flex justify-between'>
        <h1 className='text-2xl font-bold text-gray-900'>Related Content - Videos</h1>
        <CustomLink
          href={getWithBaseUrl(`/players/${slug}/videos?tournament=all`)}
          className='self-center font-bold text-brand-700'
        >
          View all
        </CustomLink>
      </div>
      <div className='mx-1 rounded-lg bg-white py-2 shadow-sm'>
        <div className='relative z-0 sm:hidden'>
          <Swiper
            pagination={{
              clickable: true,
              bulletActiveClass: 'custom-playerswiper-pagination-bullet-active',
            }}
            modules={[Pagination]}
            slidesPerView={1}
            loop
          >
            {validVideos.map((video) => {
              const url = video.link;
              const { videoId, start } = getYouTubeVideoData(url);
              return (
                <SwiperSlide
                  key={`${videoId}-${video.teamOnePlayerOneFirstName}-${video.teamTwoPlayerOneFirstName}-mobile`}
                  className='mb-6 px-2 pt-2'
                >
                  <YoutubeCards youtubeVideoId={videoId} start={start} video={video} />
                </SwiperSlide>
              );
            })}
            {validVideos.length > 1 && <SwiperNavButtons />}
          </Swiper>
        </div>
        <div className='relative hidden sm:block'>
          <Swiper
            pagination={{
              clickable: true,
              bulletActiveClass: 'custom-playerswiper-pagination-bullet-active',
            }}
            loop
            modules={[Pagination, Navigation]}
            slidesPerView={3}
            slidesPerGroup={3}
          >
            {validVideos.map((video) => {
              const url = video.link;
              const { videoId, start } = getYouTubeVideoData(url);
              return (
                <SwiperSlide
                  key={`${videoId}-${video.teamOnePlayerOneFirstName}-${video.teamTwoPlayerOneFirstName}-desktop`}
                  className='mb-6 px-2 pt-2'
                >
                  <YoutubeCards youtubeVideoId={videoId} start={start} video={video} />
                </SwiperSlide>
              );
            })}
            {validVideos.length > 3 && <SwiperNavButtons />}
          </Swiper>
        </div>
      </div>
    </>
  );
};
